import {z} from 'zod'

import {tzDateFromIso} from 'src/api/zod-helpers'

const loadEntrySourceSchema = z.enum(['office', 'driver_link'])

export const loadEntryRawSchema = z.object({
    id: z.string(),
    job_worker_assignment_id: z.string().nullable(),
    worker_name: z.string().nullable(),
    load_date: z.string(),
    load_count: z.number(),
    ticket_number: z.string().nullable(),
    entered_via: loadEntrySourceSchema,
    recorded_by_name: z.string().nullable(),
    recorded_at: z.string(),
    voided_at: z.string().nullable().optional(),
    voided_by_name: z.string().nullable().optional(),
    void_reason: z.string().nullable().optional(),
})

export const loadDriverRawSchema = z.object({
    job_worker_assignment_id: z.string(),
    worker_name: z.string(),
})

export const jobLoadsRawSchema = z.object({
    billed_per_load: z.boolean(),
    total_loads: z.number(),
    entries: z.array(loadEntryRawSchema),
    drivers: z.array(loadDriverRawSchema),
})

export type LoadEntryRaw = z.infer<typeof loadEntryRawSchema>
export type LoadDriverRaw = z.infer<typeof loadDriverRawSchema>
export type JobLoadsRaw = z.infer<typeof jobLoadsRawSchema>

export const loadDriverSchema = loadDriverRawSchema.transform((raw) => ({
    assignmentId: raw.job_worker_assignment_id,
    workerName: raw.worker_name,
}))

export const loadEntrySchema = loadEntryRawSchema.transform((raw) => ({
    id: raw.id,
    assignmentId: raw.job_worker_assignment_id,
    workerName: raw.worker_name,
    loadDate: raw.load_date,
    loadCount: raw.load_count,
    ticketNumber: raw.ticket_number,
    enteredVia: raw.entered_via,
    recordedByName: raw.recorded_by_name,
    recordedAt: tzDateFromIso.parse(raw.recorded_at),
    /** Voided entries stay listed but count no loads. */
    voidedAt: raw.voided_at ? tzDateFromIso.parse(raw.voided_at) : null,
    voidedByName: raw.voided_by_name ?? null,
    voidReason: raw.void_reason ?? null,
}))

export const jobLoadsSchema = jobLoadsRawSchema.transform((raw) => ({
    billedPerLoad: raw.billed_per_load,
    totalLoads: raw.total_loads,
    entries: raw.entries.map((entry) => loadEntrySchema.parse(entry)),
    drivers: raw.drivers.map((driver) => loadDriverSchema.parse(driver)),
}))

export type LoadDriver = z.infer<typeof loadDriverSchema>
export type LoadEntry = z.infer<typeof loadEntrySchema>
export type JobLoads = z.infer<typeof jobLoadsSchema>

export const loadEntryInputSchema = z.object({
    assignmentId: z.string().nullable(),
    loadDate: z.string(),
    loadCount: z.number().int().min(1),
    ticketNumber: z.string(),
})

export type LoadEntryInput = z.infer<typeof loadEntryInputSchema>

export const loadEntryRequestSchema = loadEntryInputSchema.transform((input) => ({
    job_worker_assignment_id: input.assignmentId,
    load_date: input.loadDate,
    load_count: input.loadCount,
    ticket_number: input.ticketNumber.trim() || null,
}))
